/**
 * Cloud Data Sync Service
 *
 * Syncs localStorage data to/from PostgreSQL backend.
 * Strategy: localStorage for fast reads + debounced push to server.
 * Conflict resolution: Last-Write-Wins (client_updated_at timestamp).
 */

import apiClient from './apiClient';

// ─── Syncable Keys ──────────────────────────────────────────────────

export const SYNC_KEYS = [
  'user_goals',
  'daily_checkins',
  'timer_sessions',
  'midday_pulses',
  'growth_moments',
  'gamification_state',
  'coach_messages',
  'coach_ai_usage',
  'notification_settings',
  'learner_profile',
  'survey_result',
  'rich_persona_profile',
] as const;

export type SyncKey = (typeof SYNC_KEYS)[number];

// ─── Internal State ─────────────────────────────────────────────────

/** Timestamp of last known update per key (ISO string) */
const localTimestamps = new Map<SyncKey, string>();

/** Keys that have been modified locally and need pushing */
const dirtyKeys = new Set<SyncKey>();

/** Debounce timer for push */
let pushTimer: ReturnType<typeof setTimeout> | null = null;

/** Whether initial pull has completed */
let initialPullDone = false;

/** Offline queue — items that failed to push */
const offlineQueue: Array<{ key: SyncKey; data: unknown; updatedAt: string }> = [];

/** Listeners for sync events */
type SyncListener = (event: 'pull_complete' | 'push_complete' | 'conflict') => void;
const listeners: SyncListener[] = [];

// ─── Helpers ────────────────────────────────────────────────────────

function isAuthenticated(): boolean {
  if (typeof window === 'undefined') return false;
  return !!localStorage.getItem('accessToken');
}

function nowISO(): string {
  return new Date().toISOString();
}

function getLocalData(key: SyncKey): unknown {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function setLocalData(key: SyncKey, data: unknown): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch {
    // Storage full
  }
}

function getTimestampKey(key: SyncKey): string {
  return `_sync_ts_${key}`;
}

function getStoredTimestamp(key: SyncKey): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(getTimestampKey(key));
}

function setStoredTimestamp(key: SyncKey, ts: string): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(getTimestampKey(key), ts);
  localTimestamps.set(key, ts);
}

// ─── Core Sync Operations ───────────────────────────────────────────

/**
 * Pull all data from server and merge into localStorage.
 * Called on app startup after authentication.
 */
async function pull(): Promise<void> {
  if (!isAuthenticated()) return;

  try {
    const response = await apiClient.get<{
      success: boolean;
      items: Array<{ key: SyncKey; data: unknown; updatedAt: string }>;
    }>('/sync/pull');

    if (!response.data.success) return;

    for (const item of response.data.items) {
      const localTs = getStoredTimestamp(item.key);
      const serverTs = item.updatedAt;

      // If server data is newer (or no local timestamp), update local
      if (!localTs || serverTs > localTs) {
        setLocalData(item.key, item.data);
        setStoredTimestamp(item.key, serverTs);
      }
      // If local is newer, mark as dirty to push back
      else if (localTs > serverTs) {
        dirtyKeys.add(item.key);
      }
    }

    // If there are keys with local data but not on server, mark dirty
    for (const key of SYNC_KEYS) {
      const localData = getLocalData(key);
      if (localData !== null && !response.data.items.some((i) => i.key === key)) {
        dirtyKeys.add(key);
      }
    }

    initialPullDone = true;
    notifyListeners('pull_complete');

    // Push any dirty keys
    if (dirtyKeys.size > 0) {
      schedulePush();
    }
  } catch (error) {
    console.warn('[SyncService] Pull failed, will retry later:', error);
    initialPullDone = true; // Don't block the app
  }
}

/**
 * Push dirty local data to server.
 */
async function push(): Promise<void> {
  if (!isAuthenticated() || dirtyKeys.size === 0) return;

  const items: Array<{ key: SyncKey; data: unknown; updatedAt: string }> = [];

  for (const key of dirtyKeys) {
    const data = getLocalData(key);
    if (data === null) continue;

    const ts = getStoredTimestamp(key) || nowISO();
    items.push({ key, data, updatedAt: ts });
  }

  if (items.length === 0) {
    dirtyKeys.clear();
    return;
  }

  try {
    const response = await apiClient.post<{
      success: boolean;
      results: Array<{ key: string; status: string }>;
    }>('/sync/push', { items });

    if (response.data.success) {
      // Clear pushed keys
      for (const result of response.data.results) {
        if (result.status === 'updated' || result.status === 'skipped') {
          dirtyKeys.delete(result.key as SyncKey);
        }
      }
      // Drain offline queue on success
      drainOfflineQueue();
      notifyListeners('push_complete');
    }
  } catch (error) {
    console.warn('[SyncService] Push failed, queuing offline:', error);
    // Move to offline queue
    for (const item of items) {
      offlineQueue.push(item);
      dirtyKeys.delete(item.key);
    }
  }
}

/**
 * Schedule a debounced push (1 second delay).
 */
function schedulePush(): void {
  if (pushTimer) clearTimeout(pushTimer);
  pushTimer = setTimeout(() => {
    pushTimer = null;
    push();
  }, 1000);
}

/**
 * Try to push queued offline items.
 */
async function drainOfflineQueue(): Promise<void> {
  if (offlineQueue.length === 0) return;

  const items = offlineQueue.splice(0, offlineQueue.length);
  try {
    await apiClient.post('/sync/push', { items });
  } catch {
    // Re-queue if still offline
    offlineQueue.push(...items);
  }
}

// ─── Public API ─────────────────────────────────────────────────────

/**
 * Mark a localStorage key as modified and schedule a sync push.
 * Call this AFTER writing to localStorage.
 */
export function markDirty(key: SyncKey): void {
  const ts = nowISO();
  setStoredTimestamp(key, ts);
  dirtyKeys.add(key);
  schedulePush();
}

/**
 * Initialize sync: pull from server, set up online/offline listeners.
 * Call once on app startup after authentication is confirmed.
 */
export function initSync(): void {
  if (typeof window === 'undefined') return;
  if (!isAuthenticated()) return;

  // Load stored timestamps
  for (const key of SYNC_KEYS) {
    const ts = getStoredTimestamp(key);
    if (ts) localTimestamps.set(key, ts);
  }

  // Initial pull
  pull();

  // Listen for online/offline events
  window.addEventListener('online', () => {
    drainOfflineQueue();
    if (dirtyKeys.size > 0) schedulePush();
  });
}

/**
 * Force a full sync (pull then push).
 */
export async function forceSync(): Promise<void> {
  await pull();
  await push();
}

/**
 * Check if initial pull is complete.
 */
export function isSyncReady(): boolean {
  return initialPullDone;
}

/**
 * Subscribe to sync events.
 */
export function onSyncEvent(listener: SyncListener): () => void {
  listeners.push(listener);
  return () => {
    const idx = listeners.indexOf(listener);
    if (idx >= 0) listeners.splice(idx, 1);
  };
}

function notifyListeners(event: 'pull_complete' | 'push_complete' | 'conflict'): void {
  for (const listener of listeners) {
    try {
      listener(event);
    } catch {
      // Don't let listener errors break sync
    }
  }
}
